import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What must a Swedish invoice (faktura) include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Swedish invoice (faktura) must include: your business name and address; your VAT number (momsregistreringsnummer) if VAT-registered; the client name and address; a unique invoice number; the invoice date; an itemized description of goods or services; the net amount, applicable VAT rate (25%, 12%, or 6%), VAT amount, and total including VAT. For B2B supplies within the EU, include the client VAT number.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the VAT (moms) rate in Sweden?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Sweden has three VAT (moms / mervärdesskatt) rates. The standard rate is 25%, which applies to most goods and services. The reduced rate of 12% applies to food, hotel accommodation, and restaurant services. A further reduced rate of 6% applies to books, newspapers, cultural events, and public transport. Exports outside Sweden are zero-rated.',
      },
    },
    {
      '@type': 'Question',
      name: 'When do Swedish businesses need to register for VAT?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'VAT (momsregistrering) registration with Skatteverket is compulsory for businesses with annual taxable turnover exceeding SEK 80,000 (from 2025, increased from SEK 30,000). Below this threshold, registration is optional. Once registered, you must file VAT returns and remit VAT quarterly or monthly.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do Swedish freelancers invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Swedish freelancers billing EU business clients apply the reverse charge — the client accounts for VAT in their country. For non-EU clients, services are generally zero-rated and no moms is charged. Include your momsregistreringsnummer and note "Omvänd skattskyldighet" (reverse charge) on EU B2B invoices. Use the international invoice type to bill in EUR, USD, or GBP with your IBAN.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this invoice generator free for Swedish users?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is completely free for Swedish businesses and freelancers. Create fakturor (invoices) with 25% moms auto-calculated, or standard invoices in SEK or EUR. Download a professional PDF instantly — no sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Percent,
    title: 'VAT Invoice (Faktura)',
    subtitle: 'For momsregistrerade Swedish businesses',
    features: ['25% moms auto-calculated', 'Momsregistreringsnummer field', 'Net + moms + total', 'Skatteverket-compliant', 'SEK currency'],
    to: '/create/vat',
    color: 'blue',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For businesses below SEK 80,000',
    features: ['No moms required', 'SEK currency', 'Custom line items', 'Logo upload', 'Payment terms'],
    to: '/create/freelancer',
    color: 'slate',
    btnClass: 'bg-slate-700 hover:bg-slate-800',
  },
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients',
    features: ['USD, EUR, GBP billing', 'Reverse charge support', 'IBAN / SWIFT fields', 'Multi-currency', 'Professional PDF'],
    to: '/create/international',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorSwedenPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Sweden: Free Faktura Maker for Swedish Freelancers | Invilic"
        description="Free invoice generator (faktura maker) for Swedish businesses and freelancers. Create moms invoices with 25% VAT or standard fakturor in SEK. PDF download free, no sign-up."
        canonical="https://www.invilic.com/invoice-generator-sweden"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇸🇪 Sweden
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Sweden
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free faktura (invoice) generator for Swedish businesses, freelancers, and sole traders. Create VAT invoices with 25% moms auto-calculated, or standard fakturor in SEK. Download professional PDF invoices instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/vat" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200">
              Create VAT Faktura <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-800 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-200">
              Create Standard Faktura <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">VAT Faktura vs Standard Faktura in Sweden</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use a VAT Faktura if:</div>
              {[
                'Your annual turnover exceeds SEK 80,000 and you are momsregistrerad',
                'Your client is a momsregistrerad business that needs to reclaim moms',
                'You bill EU business clients (omvänd skattskyldighet may apply)',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-slate-700 dark:text-slate-400 mb-3">Use a Standard Faktura if:</div>
              {[
                'Your annual turnover is below SEK 80,000 (moms exempt)',
                'You are a freelancer or sole trader not yet momsregistrerad',
                'Your client does not need a VAT breakdown',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Sweden Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your Swedish Faktura for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF faktura for Swedish businesses in under 2 minutes. No account required.</p>
          <Link to="/create/vat" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg">
            Create Faktura Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
